import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import axios from '../api/axios';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp, staggerContainer, cardItem } from '../lib/motion';

// ── Shared bits ──────────────────────────────────────────────────────

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-700',
  assigned: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-purple-50 text-purple-700',
  completed: 'bg-green-50 text-green-700',
  cancelled: 'bg-red-50 text-red-700',
  created: 'bg-gray-100 text-gray-600',
};

const StatusBadge = ({ status }) => (
  <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap capitalize ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {status?.replace('_', ' ')}
  </span>
);

const Field = ({ label, value, onChange, type = 'text', required, step, min }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label} {required && <span className="text-orange-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      step={step}
      min={min}
      className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
    />
  </div>
);

const EmptyState = ({ icon, text }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-10 text-center">
    <p className="text-3xl mb-3">{icon}</p>
    <p className="text-gray-500 text-sm">{text}</p>
  </div>
);

const AdminEngineerRequests = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [engineerRequests, setEngineerRequests] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', stock: '', image: '',
  });
  const [editingProduct, setEditingProduct] = useState(null);

  // Service list filter & pagination states
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceStatusFilter, setServiceStatusFilter] = useState('all');
  const [servicePage, setServicePage] = useState(1);
  const itemsPerPage = 8;

  // Compute Analytics Data from local states
  const totalBookings = serviceRequests.length;
  const inProgressBookings = serviceRequests.filter((r) => r.status === 'in_progress').length;
  const completedBookings = serviceRequests.filter((r) => r.status === 'completed').length;

  const doorstepBookingsCount = serviceRequests.filter((r) => r.service?.doorstep).length;
  const inShopBookingsCount = totalBookings - doorstepBookingsCount;

  // Find low stock products
  const lowStockProducts = products.filter((p) => p.stock !== undefined && p.stock < 5);

  // Compute top printer models
  const printerModelCounts = useMemo(() => {
    const counts = {};
    serviceRequests.forEach((r) => {
      const model = r.service?.printerModel?.trim();
      if (model) {
        counts[model] = (counts[model] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [serviceRequests]);

  // Compute filtered service list
  const filteredServiceRequests = useMemo(() => {
    return serviceRequests.filter((r) => {
      const matchesStatus = serviceStatusFilter === 'all' || r.status === serviceStatusFilter;
      const q = serviceSearch.toLowerCase().trim();
      const matchesQuery =
        q === '' ||
        (r.orderNumber && r.orderNumber.toLowerCase().includes(q)) ||
        (r._id && r._id.toLowerCase().includes(q)) ||
        (r.customerName && r.customerName.toLowerCase().includes(q)) ||
        (r.customerPhone && r.customerPhone.toLowerCase().includes(q)) ||
        (r.service?.printerModel && r.service.printerModel.toLowerCase().includes(q));
      return matchesStatus && matchesQuery;
    });
  }, [serviceRequests, serviceSearch, serviceStatusFilter]);

  // Compute paginated service list
  const paginatedServiceRequests = useMemo(() => {
    const start = (servicePage - 1) * itemsPerPage;
    return filteredServiceRequests.slice(start, start + itemsPerPage);
  }, [filteredServiceRequests, servicePage]);

  const totalServicePages = Math.max(1, Math.ceil(filteredServiceRequests.length / itemsPerPage));

  const fetchData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const [engineerRes, serviceRes, engineersRes, productsRes] = await Promise.all([
        axios.get('/engineers/requests'),
        axios.get('/orders'),
        axios.get('/engineers'),
        axios.get('/products'),
      ]);

      if (engineerRes.data.success) setEngineerRequests(engineerRes.data.data);
      if (serviceRes.data.success) setServiceRequests(serviceRes.data.data);
      if (engineersRes.data.success) setEngineers(engineersRes.data.data);
      if (productsRes.data) setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data.');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);

    // Poll every 30 seconds for new requests and updates silently
    const interval = setInterval(() => {
      fetchData(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchData]);

  // ─── Engineer Handlers (unchanged logic) ──────────────────────────

  const handleApproveEngineer = async (id) => {
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/engineers/requests/${id}/approve`);
      if (response.data.success) {
        toast.success('Engineer request approved successfully.');
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to approve request.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve request.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectEngineer = async (id) => {
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/engineers/requests/${id}/reject`);
      if (response.data.success) {
        toast.success('Engineer request rejected successfully.');
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to reject request.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject request.');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Service Handlers (unchanged logic) ───────────────────────────

  const handleAssignEngineer = async (orderId, engineerId) => {
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/orders/${orderId}/assign-engineer`, { engineerId });
      if (response.data.success) {
        toast.success('Engineer assigned successfully.');
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to assign engineer.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign engineer.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassignEngineer = async (orderId) => {
    if (actionLoading) return;
    if (!window.confirm('Remove the assigned engineer and reset this order to "created"?')) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/orders/${orderId}/unassign-engineer`);
      if (response.data.success) {
        toast.success('Engineer unassigned. Order reset to created.');
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to unassign engineer.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unassign engineer.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, status) => {
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/orders/${orderId}/status`, { status });
      if (response.data.success) {
        toast.success(`Order status updated to "${status}".`);
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to update status.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update status.');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Product Handlers (unchanged logic) ───────────────────────────

  const getProductData = () => ({
    name: productForm.name,
    description: productForm.description,
    price: parseFloat(productForm.price),
    stock: parseInt(productForm.stock, 10),
    images: productForm.image ? [productForm.image] : [],
  });

  const resetProductForm = () => {
    setProductForm({ name: '', description: '', price: '', stock: '', image: '' });
    setEditingProduct(null);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.post('/products', getProductData());
      if (response.data.success) {
        toast.success('Product added successfully.');
        resetProductForm();
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to add product.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add product.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (actionLoading) return;
    try {
      setActionLoading(true);
      const response = await axios.put(`/products/${editingProduct}`, getProductData());
      if (response.data.success) {
        toast.success('Product updated successfully.');
        resetProductForm();
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to update product.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update product.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditProduct = (product) => {
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price,
      stock: product.stock,
      image: product.images?.[0] || '',
    });
    setEditingProduct(product._id);
  };

  const handleDeleteProduct = async (id) => {
    if (actionLoading) return;
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      setActionLoading(true);
      const response = await axios.delete(`/products/${id}`);
      if (response.data?.success !== false) {
        toast.success('Product deleted successfully.');
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to delete product.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete product.');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Helpers (unchanged) ───────────────────────────────────────────

  const isVideoUrl = (url) =>
    url.includes('.mp4') || url.includes('.webm') || url.includes('.ogg') || url.includes('video');

  const openModal = (url) => {
    setSelectedMedia(url);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedMedia(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-6">
        <div className="max-w-5xl mx-auto animate-pulse flex flex-col gap-6">
          <div className="flex flex-col gap-2 mb-4">
            <div className="h-3 bg-gray-200 rounded w-16" />
            <div className="h-8 bg-gray-200 rounded w-52" />
          </div>
          <div className="flex gap-2 mb-4 border-b border-gray-200 pb-3">
            <div className="h-8 bg-gray-200 rounded w-24" />
            <div className="h-8 bg-gray-200 rounded w-24" />
            <div className="h-8 bg-gray-200 rounded w-24" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-36 bg-white border border-gray-100 rounded-xl" />
            <div className="h-36 bg-white border border-gray-100 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  const approvedEngineers = engineers;
  const pendingEngineerCount = engineerRequests.filter((r) => r.status === 'pending').length;
  const unassignedServiceCount = serviceRequests.filter((r) => !r.engineer).length;

  const tabs = [
    { key: 'analytics', label: 'Analytics', count: null },
    { key: 'engineers', label: 'Engineer requests', count: pendingEngineerCount },
    { key: 'services', label: 'Service requests', count: unassignedServiceCount },
    { key: 'products', label: 'Products', count: null },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-10">

        {/* Header */}
        <motion.div className="mb-7" initial="hidden" animate="show" variants={fadeUp}>
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Admin</p>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-7 border-b border-gray-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.key ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <span className="flex items-center gap-1.5">
                {tab.label}
                {tab.count > 0 && (
                  <span className="bg-blue-600 text-white text-[10px] font-semibold rounded-full min-w-[18px] px-1 py-0.5 flex items-center justify-center">
                    {tab.count}
                  </span>
                )}
              </span>
              {activeTab === tab.key && (
                <motion.div
                  layoutId="adminTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">

          {/* ── Analytics Tab ────────────────────────────────── */}
          {activeTab === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col gap-6"
            >
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Total Bookings", value: totalBookings, icon: "📋", color: "text-blue-600 bg-blue-50" },
                  { label: "In Progress", value: inProgressBookings, icon: "⚙️", color: "text-yellow-600 bg-yellow-50" },
                  { label: "Completed Repairs", value: completedBookings, icon: "✅", color: "text-green-600 bg-green-50" },
                  { label: "Low Stock Alert", value: lowStockProducts.length, icon: "⚠️", color: lowStockProducts.length > 0 ? "text-red-600 bg-red-50" : "text-gray-600 bg-gray-50" },
                ].map((stat, idx) => (
                  <div key={idx} className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex items-center gap-4">
                    <span className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${stat.color}`}>
                      {stat.icon}
                    </span>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Left Column: Fulfillment & Models */}
                <div className="md:col-span-2 flex flex-col gap-6">
                  {/* Fulfillment breakdown */}
                  <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span>🏠</span> Fulfillment Breakdown
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                          <span>Doorstep Service ({doorstepBookingsCount})</span>
                          <span className="font-semibold text-gray-700">{totalBookings > 0 ? Math.round((doorstepBookingsCount / totalBookings) * 100) : 0}%</span>
                        </div>
                        <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                          <div
                            className="bg-blue-600 h-full rounded-full transition-all duration-500"
                            style={{ width: `${totalBookings > 0 ? (doorstepBookingsCount / totalBookings) * 100 : 0}%` }}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                          <span>In-Shop dropoff ({inShopBookingsCount})</span>
                          <span className="font-semibold text-gray-700">{totalBookings > 0 ? Math.round((inShopBookingsCount / totalBookings) * 100) : 0}%</span>
                        </div>
                        <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                          <div
                            className="bg-indigo-500 h-full rounded-full transition-all duration-500"
                            style={{ width: `${totalBookings > 0 ? (inShopBookingsCount / totalBookings) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Printer models */}
                  <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span>🖨️</span> Top Printer Models Serviced
                    </h3>

                    {printerModelCounts.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-6">No printer data available.</p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {printerModelCounts.map(([model, count], idx) => (
                          <div key={idx} className="flex items-center justify-between border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                            <span className="text-xs text-gray-700 font-medium">{model}</span>
                            <span className="text-xs bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full font-semibold">{count} repairs</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column: Inventory Alerts */}
                <div>
                  {/* Inventory alert */}
                  <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span>📦</span> Low Stock Alerts
                    </h3>

                    {lowStockProducts.length === 0 ? (
                      <div className="text-center py-8">
                        <span className="text-2xl">🎉</span>
                        <p className="text-xs text-gray-500 mt-2">All product stock levels are healthy.</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3.5">
                        {lowStockProducts.map((p) => (
                          <div key={p._id} className="flex items-center justify-between border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                            <div className="max-w-[70%]">
                              <p className="text-xs font-semibold text-gray-800 line-clamp-1">{p.name}</p>
                              <p className="text-[10px] text-gray-400 capitalize">Cat: {p.category || 'N/A'}</p>
                            </div>
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${p.stock === 0 ? 'bg-red-50 text-red-700' : 'bg-orange-50 text-orange-700'}`}>
                              {p.stock} left
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Engineer Requests Tab ──────────────────────────── */}
          {activeTab === 'engineers' && (
            <motion.div
              key="engineers"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {engineerRequests.length === 0 ? (
                <EmptyState icon="🔧" text="No engineer requests yet." />
              ) : (
                <motion.div
                  className="flex flex-col gap-3"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="show"
                >
                  {engineerRequests.map((request) => (
                    <motion.div
                      key={request._id}
                      variants={cardItem}
                      className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{request.fullName}</p>
                          <p className="text-xs text-gray-400">{request.email}</p>
                        </div>
                        <StatusBadge status={request.status} />
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {request.skills?.map((skill) => (
                          <span key={skill} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>

                      <p className="text-sm text-gray-500 leading-relaxed mb-4">{request.experience}</p>

                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => handleApproveEngineer(request._id)}
                            disabled={actionLoading}
                            className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                          >
                            Approve
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => handleRejectEngineer(request._id)}
                            disabled={actionLoading}
                            className="flex-1 sm:flex-none bg-white border border-gray-200 hover:border-red-300 hover:text-red-600 disabled:opacity-50 text-gray-600 text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                          >
                            Reject
                          </motion.button>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ── Service Requests Tab ───────────────────────────── */}
          {activeTab === 'services' && (
            <motion.div
              key="services"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {/* Search & Filter Controls */}
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                {/* Search box */}
                <div className="relative flex-1">
                  <svg
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={serviceSearch}
                    onChange={(e) => {
                      setServiceSearch(e.target.value);
                      setServicePage(1);
                    }}
                    placeholder="Search by Ref #, name, phone, printer model…"
                    className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  />
                </div>

                {/* Status Dropdown */}
                <select
                  value={serviceStatusFilter}
                  onChange={(e) => {
                    setServiceStatusFilter(e.target.value);
                    setServicePage(1);
                  }}
                  className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  <option value="all">All Statuses</option>
                  <option value="created">Created (Pending)</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {filteredServiceRequests.length === 0 ? (
                <EmptyState icon="🖨️" text="No service requests found." />
              ) : (
                <div>
                  <motion.div
                    className="flex flex-col gap-3"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="show"
                  >
                    {paginatedServiceRequests.map((request) => (
                      <motion.div
                        key={request._id}
                        variants={cardItem}
                        className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                          <div>
                            <p className="text-xs text-gray-400 font-mono">{request.orderNumber || request._id}</p>
                            <p className="text-sm font-semibold text-gray-900 mt-0.5">{request.customerName}</p>
                            <p className="text-xs text-gray-400">{request.customerPhone}</p>
                          </div>
                          <StatusBadge status={request.status} />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm">
                          <div>
                            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Printer</p>
                            <p className="text-gray-800">{request.service?.printerModel || '—'}</p>
                          </div>
                          <div>
                            <p className="text-[11px] text-gray-400 uppercase tracking-wide">Service</p>
                            <p className="text-gray-800">{request.service?.doorstep ? 'Doorstep' : 'In-shop'}</p>
                          </div>
                        </div>

                        <p className="text-sm text-gray-500 leading-relaxed mb-3">{request.service?.description}</p>

                        {request.service?.media?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {request.service.media.map((url) =>
                              isVideoUrl(url) ? (
                                <video
                                  key={url}
                                  src={url}
                                  muted
                                  className="w-14 h-14 object-cover rounded-lg cursor-pointer hover:opacity-80 transition"
                                  onClick={() => openModal(url)}
                                />
                              ) : (
                                <img
                                  key={url}
                                  src={url}
                                  alt="Service media"
                                  className="w-14 h-14 object-cover rounded-lg cursor-pointer hover:opacity-80 transition"
                                  onClick={() => openModal(url)}
                                />
                              )
                            )}
                          </div>
                        )}

                        <div className="flex flex-col gap-3 pt-3 border-t border-gray-50">
                          {/* Engineer assignment row */}
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[11px] text-gray-400 uppercase tracking-wide w-full sm:w-auto">Engineer</span>
                            <select
                              value={request.engineer?._id || ''}
                              disabled={actionLoading}
                              onChange={(e) => {
                                if (e.target.value) handleAssignEngineer(request._id, e.target.value);
                              }}
                              className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                              <option value="">{request.engineer ? `${request.engineer.name} (reassign…)` : 'Assign engineer…'}</option>
                              {approvedEngineers
                                .filter((e) => e._id !== request.engineer?._id)
                                .map((engineer) => (
                                  <option key={engineer._id} value={engineer._id}>
                                    {engineer.name} ({engineer.email})
                                  </option>
                                ))}
                            </select>
                            {request.engineer && (
                              <button
                                onClick={() => handleUnassignEngineer(request._id)}
                                disabled={actionLoading}
                                className="text-xs font-medium text-red-500 hover:text-red-700 disabled:opacity-50 transition-colors whitespace-nowrap"
                              >
                                Unassign
                              </button>
                            )}
                          </div>

                          {/* Status override row */}
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[11px] text-gray-400 uppercase tracking-wide w-full sm:w-auto">Status</span>
                            <select
                              value={request.status}
                              disabled={actionLoading}
                              onChange={(e) => {
                                if (e.target.value !== request.status) handleUpdateStatus(request._id, e.target.value);
                              }}
                              className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                              {['created', 'assigned', 'in_progress', 'completed', 'cancelled'].map((s) => (
                                <option key={s} value={s}>{s.replace('_', ' ')}</option>
                              ))}
                            </select>
                          </div>

                          {/* Date */}
                          <p className="text-xs text-gray-400">
                            {request.createdAt &&
                              new Date(request.createdAt).toLocaleDateString('en-IN', {
                                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                              })}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Pagination Footer (Correctly Placed Inside Services View Now) */}
                  {totalServicePages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-5">
                      <button
                        onClick={() => setServicePage(p => Math.max(1, p - 1))}
                        disabled={servicePage === 1}
                        className="px-3.5 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
                      >
                        ← Previous
                      </button>
                      <span className="text-xs text-gray-400 font-medium">
                        Page {servicePage} of {totalServicePages}
                      </span>
                      <button
                        onClick={() => setServicePage(p => Math.min(totalServicePages, p + 1))}
                        disabled={servicePage === totalServicePages}
                        className="px-3.5 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* ── Products Tab ───────────────────────────────────── */}
          {activeTab === 'products' && (
            <motion.div
              key="products"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm mb-6">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                  {editingProduct ? 'Edit product' : 'Add new product'}
                </p>
                <form onSubmit={editingProduct ? handleUpdateProduct : handleProductSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      label="Name"
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      required
                    />
                    <Field
                      label="Price (₹)"
                      type="number"
                      step="0.01"
                      min="0"
                      value={productForm.price}
                      onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                      required
                    />
                    <Field
                      label="Stock"
                      type="number"
                      min="0"
                      value={productForm.stock}
                      onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                      required
                    />
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Image URL</label>
                      <input
                        type="url"
                        value={productForm.image}
                        onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                        className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      />
                      {productForm.image && (
                        <img
                          src={productForm.image}
                          alt="Preview"
                          className="mt-1 w-16 h-16 object-cover rounded-lg border border-gray-100"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="md:col-span-2 flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Description <span className="text-orange-500">*</span>
                      </label>
                      <textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        rows={3}
                        required
                        className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={actionLoading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors"
                    >
                      {actionLoading ? 'Saving…' : editingProduct ? 'Update product' : 'Add product'}
                    </motion.button>
                    {editingProduct && (
                      <button
                        type="button"
                        onClick={resetProductForm}
                        className="bg-white border border-gray-200 hover:border-gray-300 text-gray-600 text-sm font-medium px-5 py-2.5 rounded-xl transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Existing products ({products.length})
              </p>
              {products.length === 0 ? (
                <EmptyState icon="📦" text="No products yet." />
              ) : (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="show"
                >
                  {products.map((product) => (
                    <motion.div
                      key={product._id}
                      variants={cardItem}
                      className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex gap-3"
                    >
                      {product.images?.length > 0 ? (
                        <img src={product.images[0]} alt={product.name} className="w-16 h-16 object-cover rounded-lg shrink-0" />
                      ) : (
                        <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-[10px] text-gray-300">No image</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{product.name}</p>
                        <p className="text-xs text-gray-400">₹{product.price} · {product.stock} in stock</p>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleEditProduct(product)}
                            disabled={actionLoading}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product._id)}
                            disabled={actionLoading}
                            className="text-xs font-medium text-red-500 hover:text-red-600 disabled:opacity-50 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Media Modal ── */}
      <AnimatePresence>
        {modalOpen && selectedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white p-4 rounded-xl max-w-2xl max-h-full overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Media preview</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-700 text-xl leading-none transition-colors">
                  &times;
                </button>
              </div>
              <div className="flex justify-center">
                {isVideoUrl(selectedMedia) ? (
                  <video src={selectedMedia} controls className="max-w-full max-h-96 rounded-lg" />
                ) : (
                  <img src={selectedMedia} alt="Media preview" className="max-w-full max-h-96 object-contain rounded-lg" />
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminEngineerRequests;